import React from 'react'
import type { ChatThread } from '../../types'
import {
  formatUSPhoneForDisplay,
  normalizePhoneNumber,
} from '@super/shared-types'
import { getThreadEventPreview } from '../../utils/thread-event-preview'
import { formatThreadListTime } from '@/utils/date-formatters'
import { Avatar } from '@/components/common/Avatar'
import { HighlightedText } from '../MessageList/HighlightedText'

interface ThreadPreviewProps {
  thread: ChatThread
  isSelected: boolean
  onClick: () => void
  searchQuery?: string
  searchSnippet?: string
  searchMetadata?: {
    originalThreadId: string
    sessionId: string
    eventTime: Date
  }
}

export const ThreadPreview: React.FC<ThreadPreviewProps> = ({
  thread,
  isSelected,
  onClick,
  searchQuery,
  searchSnippet,
  searchMetadata,
}) => {
  const getLastEventPreview = () => {
    // If we have a search snippet, use that instead of the last event
    if (searchSnippet) {
      return searchSnippet
    }
    if (!thread.lastThreadEvent) return 'No activity yet'
    return getThreadEventPreview(thread.lastThreadEvent)
  }

  const getContactInfo = () => {
    // Use threadType to determine what to display
    if (thread.threadType === 'PHONE') {
      if (!thread.participant.phone) return 'No phone number'
      // Normalize phone from OpenAI's 10-digit format to E.164 before formatting
      const normalizedPhone =
        normalizePhoneNumber(thread.participant.phone) ||
        thread.participant.phone
      return formatUSPhoneForDisplay(normalizedPhone)
    }
    if (thread.threadType === 'EMAIL') {
      return thread.participant.email || 'No email'
    }
    // Fallback: show whatever contact info is available
    if (thread.participant.email) {
      return thread.participant.email
    }
    if (thread.participant.phone) {
      const normalizedPhone =
        normalizePhoneNumber(thread.participant.phone) ||
        thread.participant.phone
      return formatUSPhoneForDisplay(normalizedPhone)
    }
    return 'No contact info'
  }

  return (
    <div
      data-thread-preview
      className={`px-5 py-3 md:px-3 md:py-2 lg:px-5 lg:py-3 border-b border-gray-200 dark:border-border cursor-pointer transition-colors duration-200 flex items-start gap-3 ${
        isSelected
          ? 'bg-gray-900 text-white hover:bg-gray-800'
          : 'hover:bg-gray-100 dark:hover:bg-muted'
      }`}
      onClick={onClick}
    >
      <Avatar
        email={thread.participant.email}
        name={thread.participant.name}
        size={40}
        className={isSelected ? 'bg-gray-700 text-white' : ''}
      />

      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div
          className={`font-semibold text-sm leading-tight ${
            isSelected ? 'text-white' : 'text-gray-900 dark:text-foreground'
          }`}
        >
          {thread.participant.name &&
          thread.participant.name !== 'Unknown Contact'
            ? thread.participant.name
            : getContactInfo()}
        </div>
        <div
          className={`text-sm leading-snug overflow-hidden text-ellipsis line-clamp-2 ${
            isSelected
              ? 'text-white/90'
              : 'text-gray-600 dark:text-muted-foreground'
          }`}
        >
          <HighlightedText
            text={getLastEventPreview()}
            searchQuery={searchQuery}
          />
        </div>
      </div>

      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <div
          className={`text-xs whitespace-nowrap ${
            isSelected
              ? 'text-white/70'
              : 'text-gray-500 dark:text-muted-foreground'
          }`}
        >
          {searchMetadata
            ? formatThreadListTime(searchMetadata.eventTime)
            : thread.lastThreadEvent
              ? formatThreadListTime(thread.lastThreadEvent.eventTime)
              : formatThreadListTime(thread.createdAt)}
        </div>
      </div>
    </div>
  )
}
